/**
 * Хуки вокруг /api/auth/*.
 *
 * H4: backend ставит HttpOnly cookie, фронт сохраняет только user. Если
 * signup пришёл с requires_confirmation=true, в response нет user/token -
 * фронт показывает «check email»
 *
 * H5/H6: signup может вернуть:
 *   - {user, token} - dev-mode, mailer не настроен → юзер сразу залогинен
 *   - {requires_confirmation: true} - prod-mode, ждём email-подтверждения
 *   и НЕ создаём сессию (cookie не пришёл)
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth, type CurrentUser } from "@/stores/auth";

export interface SignupResponse {
  user: CurrentUser | null;
  token?: string | null;            // legacy, фронт игнорирует
  requires_confirmation: boolean;
  status: string;
}

interface LoginResponse {
  user: CurrentUser;
  token?: string | null;
}

export function useSignup() {
  const qc = useQueryClient();
  const setUser = useAuth((s) => s.setUser);
  return useMutation({
    mutationFn: (body: { email: string; password: string; display_name?: string; ref?: string }) =>
      api.post<SignupResponse>("/auth/signup", body),
    onSuccess: (data) => {
      if (data.user) {
        setUser(data.user);
        qc.invalidateQueries();
      }
    },
  });
}

export function useLogin() {
  const qc = useQueryClient();
  const setUser = useAuth((s) => s.setUser);
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      api.post<LoginResponse>("/auth/login", body),
    onSuccess: (data) => {
      setUser(data.user);
      qc.invalidateQueries();
    },
  });
}

/** Гостевой вход: одна кнопка, без email/пароля. Бэк создаёт гостевой
 * профиль, выдаёт несколько бесплатных кредитов и ставит cookie-сессию. */
export function useGuestLogin() {
  const qc = useQueryClient();
  const setUser = useAuth((s) => s.setUser);
  return useMutation({
    mutationFn: () => api.post<LoginResponse>("/auth/guest"),
    onSuccess: (data) => {
      setUser(data.user);
      qc.invalidateQueries();
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  const logout = useAuth((s) => s.logout);
  return useMutation({
    mutationFn: () => api.post<void>("/auth/logout"),
    onSettled: () => {
      // Чистим UI + кеши даже если запрос упал (cookie могла протухнуть -
      // backend ответит 401, но мы всё равно хотим logout-эффект на UI)
      logout();
      qc.clear();
    },
  });
}

// --- password reset / email confirm --------------------------------------

/** Шаг 1: запрос сброса. Бэк всегда отвечает 202 (защита от перечисления). */
export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (body: { email: string }) =>
      api.post<{ status: string }>("/auth/request-password-reset", body),
  });
}

/** Шаг 2: задать новый пароль по токену из письма. Сразу логинит (cookie). */
export function useResetPassword() {
  const qc = useQueryClient();
  const setUser = useAuth((s) => s.setUser);
  return useMutation({
    mutationFn: (body: { token: string; new_password: string }) =>
      api.post<LoginResponse>("/auth/reset-password", body),
    onSuccess: (data) => {
      if (data.user) setUser(data.user);
      qc.invalidateQueries();
    },
  });
}

/** Подтверждение email - возвращает session, логинит. */
export function useConfirmEmail() {
  const qc = useQueryClient();
  const setUser = useAuth((s) => s.setUser);
  return useMutation({
    mutationFn: (body: { token: string }) =>
      api.post<LoginResponse>("/auth/confirm-email", body),
    onSuccess: (data) => {
      if (data?.user) setUser(data.user);
      qc.invalidateQueries();
    },
  });
}
